import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Spin } from 'antd';
import { FctConfigProvider } from './estilo-corporativo-fct';
import { AuthProvider } from './contexts/AuthContext';
import { PermissionProvider, usePermissions } from './contexts/PermissionContext';
import ProtectedRoute from './components/Auth/ProtectedRoute';
import RequireResource from './components/Auth/RequireResource';
import MainLayout from './components/Layout/MainLayout';
import ExternalLayout from './components/Layout/ExternalLayout';
import HomePage from './pages/HomePage';
import TransmisionesPage from './pages/TransmisionesPage';
import OperationDetailPage from './pages/OperationDetailPage';
import KardioChatPage from './pages/KardioChatPage';
import AgentPage from './pages/AgentPage';
import ReconciliacionPage from './pages/ReconciliacionPage';
import BrevoSyncPage from './pages/BrevoSyncPage';
import BrevoSyncSistemasPage from './pages/BrevoSyncSistemasPage';
import MejoraCU2Page from './pages/MejoraCU2Page';
import VinculacionCU2Page from './pages/VinculacionCU2Page';
import AltasGestionPage from './pages/AltasGestionPage';
import AltasCasoPage from './pages/AltasCasoPage';
import SeguimientoFDocPage from './pages/SeguimientoFDocPage';
import './App.css';

/**
 * Decide el layout de la bandeja Mejora CU2 según el tipo de usuario:
 * - Externo (Smartup): tiene GCvsc.MejoraCU2.Read pero NO GCvsc.Menu.Read → ExternalLayout
 *   (cabecera "Mejora CU2 · Smartup", sin menú interno).
 * - Interno (FCT): tiene GCvsc.Menu.Read → MainLayout con menú.
 * Es una ruta de nivel superior, fuera del árbol con MainLayout.
 */
const MejoraCU2Route: React.FC = () => {
  const { hasPermission, isLoading } = usePermissions();
  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <Spin size="large" />
      </div>
    );
  }
  const esExterno = !hasPermission('GCvsc.Menu.Read');
  const page = (
    <RequireResource resource="GCvsc.MejoraCU2.Read"><MejoraCU2Page /></RequireResource>
  );
  return esExterno
    ? <ExternalLayout>{page}</ExternalLayout>
    : <MainLayout>{page}</MainLayout>;
};

/**
 * Ruta raíz: el Home interno para FCT; para usuarios externos (sin GCvsc.Menu.Read
 * pero con acceso a Mejora CU2) redirige a su bandeja, evitando el Home en blanco.
 */
const HomeRoute: React.FC = () => {
  const { hasPermission, isLoading } = usePermissions();
  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
        <Spin size="large" />
      </div>
    );
  }
  if (!hasPermission('GCvsc.Menu.Read') && hasPermission('GCvsc.MejoraCU2.Read')) {
    return <Navigate to="/mejora-cu2" replace />;
  }
  return <HomePage />;
};

function App() {
  return (
    <FctConfigProvider>
      <AuthProvider>
        <Router basename={process.env.PUBLIC_URL} future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <ProtectedRoute>
            <PermissionProvider>
              <Routes>
                {/* Mejora CU2: layout externo (Smartup) o MainLayout (FCT) según permisos (MCU2-T05) */}
                <Route path="/mejora-cu2" element={<MejoraCU2Route />} />
                {/* Modo normal: con MainLayout */}
                <Route path="/*" element={
                  <MainLayout>
                    <Routes>
                      <Route path="/" element={<HomeRoute />} />
                      <Route path="/transmisiones" element={
                        <RequireResource resource="GCvsc.Transmisiones.Read"><TransmisionesPage /></RequireResource>
                      } />
                      <Route path="/operations/:id" element={
                        <RequireResource resource="GCvsc.Transmisiones.Read"><OperationDetailPage /></RequireResource>
                      } />
                      <Route path="/chat" element={
                        <RequireResource resource="GCvsc.KardioChat.Read"><KardioChatPage /></RequireResource>
                      } />
                      <Route path="/agent" element={
                        <RequireResource resource="GCvsc.Agente.Read"><AgentPage /></RequireResource>
                      } />
                      <Route path="/reconciliacion" element={
                        <RequireResource resource="GCvsc.Reconciliacion.Read"><ReconciliacionPage /></RequireResource>
                      } />
                      <Route path="/vinculacion-cu2" element={
                        <RequireResource resource="GCvsc.Reconciliacion.Read"><VinculacionCU2Page /></RequireResource>
                      } />
                      <Route path="/brevo-sync" element={
                        <RequireResource resource="GCvsc.BrevoSync.Read"><BrevoSyncPage /></RequireResource>
                      } />
                      <Route path="/brevo-sync/sistemas" element={
                        <RequireResource resource="GCvsc.BrevoSync.Read"><BrevoSyncSistemasPage /></RequireResource>
                      } />
                      {/* F1.4 — Aplicación de validación de altas (GESCL-T01, tramo 5).
                          Sustituye a la UI mínima de T30 (AltasClientesPage, absorbida) y salda
                          la deuda de la ruta /altas sin RequireResource (RF-12): recurso
                          Clientes.Altas.Read (nomenclatura de dominio, D-GCVSC-013). */}
                      <Route path="/altas" element={
                        <RequireResource resource="Clientes.Altas.Read"><AltasGestionPage /></RequireResource>
                      } />
                      <Route path="/altas/:idCaso" element={
                        <RequireResource resource="Clientes.Altas.Read"><AltasCasoPage /></RequireResource>
                      } />
                      {/* Seguimiento de documentos en firma digital (FDoc + Signaturit). Sin
                          RequireResource por el mismo motivo que /altas: su recurso ACL aún no
                          existe en el backend, y además la regla de visibilidad por rol sigue sin
                          cerrarse (ver SeguimientoFdocVisibilidad en el backend). Protegida solo
                          por ProtectedRoute (MSAL). */}
                      <Route path="/seguimiento-fdoc" element={<SeguimientoFDocPage />} />
                    </Routes>
                  </MainLayout>
                } />
              </Routes>
            </PermissionProvider>
          </ProtectedRoute>
        </Router>
      </AuthProvider>
    </FctConfigProvider>
  );
}

export default App;
